import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { Wrench, Calendar, Car, User } from 'lucide-react';
import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useShopTimezone } from '@/contexts/TimezoneContext';
import { cn } from '@/lib/utils';

interface SpecialtyBay {
  id: string;
  name: string;
  bay_category: string;
  bay_type: string;
}

interface TicketWithJobs {
  id: string;
  customer_name: string;
  customer_phone: string | null;
  type: string;
  scheduled_start_time: string | null;
  start_time: string;
  predicted_minutes: number | null;
  status: string;
  vehicle: any;
  jobs: any[];
  assigned_tech_id: string | null;
  technician?: {
    id: string;
    name: string;
  } | null;
}

interface SpecialtySection {
  bayCategory: string;
  bayName: string;
  tickets: TicketWithJobs[];
}

interface SpecialtyBaysProps {
  embedded?: boolean;
  selectedDate?: Date;
}

// Check if a job matches a bay type
const matchesBayType = (bayType: string, job: any): boolean => {
  const category = job.category?.toLowerCase();
  const requirement = job.bay_requirement?.toLowerCase();
  
  switch (bayType?.toLowerCase()) {
    case 'alignment':
      return category === 'alignment' || requirement === 'alignment' || requirement === 'specialty';
    case 'adas':
      return category === 'adas' || requirement === 'adas' || requirement === 'specialty';
    case 'alignment-adas':
      // Alignment-ADAS bay handles BOTH alignment AND adas jobs
      return category === 'alignment' || category === 'adas' || 
             requirement === 'alignment' || requirement === 'adas' || requirement === 'specialty';
    case 'wash':
      return category === 'wash';
    case 'lift':
      return requirement === 'lift';
    case 'flat':
      return requirement === 'flat';
    default:
      return requirement === bayType?.toLowerCase();
  }
};

// Filter jobs for display based on bay category
const filterJobsForDisplay = (jobs: any[], bayCategory: string): any[] => {
  return jobs.filter((job: any) => matchesBayType(bayCategory, job));
};

export default function SpecialtyBays({ embedded = false, selectedDate }: SpecialtyBaysProps) {
  // E2EFIX (2026-10-19): the board day as a STRING. The prop used to default to `new Date()`, a new object on
  // every render; with `selectedDate` in the effect deps, the standalone `/specialty-bays` route (which passes no
  // date) refetched and rebuilt its realtime channel on every render and never left "Loading specialty tasks...".
  // The embedded Dashboard tab passes a stable Date, which is why only the route was stuck.
  const selectedDay = format(selectedDate ?? new Date(), 'yyyy-MM-dd');
  const [specialtySections, setSpecialtySections] = useState<SpecialtySection[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { activeShop } = useAuth();
  const { shopTimezone } = useShopTimezone();
  
  // CORE04-005: Dead dateStart/dateEnd removed — were computed but never used

  const fetchData = async () => {
    if (!activeShop?.id) return;
    
    try {
      setLoading(true);
      
      // Fetch configured specialty bays (excluding general, work-area, storage, front-of-house, and flat bay types)
      const { data: specialtyBays, error: baysError } = await supabase
        .from('shop_bays')
        .select('id, name, bay_category, bay_type')
        .eq('shop_id', activeShop.id)
        .neq('bay_category', 'general')
        .neq('bay_category', 'parking')
        .not('bay_category', 'in', '("work-area","storage","front-of-house")')
        .neq('bay_type', 'flat')
        .eq('is_active', true);

      if (baysError) throw baysError;

      if (!specialtyBays || specialtyBays.length === 0) {
        setSpecialtySections([]);
        return;
      }

      // OPTIMIZED: Batch fetch all data in parallel instead of N+1 queries
      const [ticketsResult, techniciansResult] = await Promise.all([
        // Fetch tickets for the selected date with vehicle data in one query
        supabase
          .from('tickets')
          .select(`
            id, 
            customer_name, 
            customer_phone, 
            type, 
            status, 
            vehicle_id, 
            assigned_tech_id, 
            scheduled_start_time, 
            start_time, 
            predicted_minutes,
            scheduled_date,
            vehicles (year, make, model)
          `)
          .eq('shop_id', activeShop.id)
          .neq('status', 'Deleted')
          // CST-A2: drop dead status values 'scheduled', 'in_progress',
          // 'finished', 'unscheduled', 'waiter_released'. None of these
          // exist in the canonical 6-status enum
          // {Active, Paused, Completed, Blocked, Deleted, Cancelled}
          // enforced by `tickets_status_check` (migration 20260424000000),
          // so the prior IN list silently degenerated to `status = 'Active'`
          // — meaning Paused + Completed tickets on specialty bays were
          // invisible on this page even when their work actually happened.
          // Mirrors the IV-D-29 R157-MED-1 / R157-MED-3 / CST-A1 cleanup
          // pattern applied to CreateTicket.tsx, ParkingSpotPickerDialog.tsx,
          // and Birdseye.tsx — narrow to the canonical 3-value active set.
          // Behavior FIX (not no-op): Paused + Completed specialty-bay
          // tickets now correctly surface on the SpecialtyBays page.
          .in('status', ['Active', 'Paused', 'Completed'])
          .gte('scheduled_date', selectedDay)
          .lte('scheduled_date', selectedDay),
        
        // Fetch ALL technicians for the shop in one query
        supabase
          .from('technicians')
          .select('id, name')
          .eq('shop_id', activeShop.id)
      ]);

      if (ticketsResult.error) throw ticketsResult.error;
      
      const tickets = ticketsResult.data || [];
      const technicians = techniciansResult.data || [];
      
      // Build technician lookup map
      const techMap = new Map(technicians.map(t => [t.id, t]));

      // Get all ticket IDs
      const ticketIds = tickets.map(t => t.id);
      
      // Fetch ALL ticket_jobs with job info in one query
      const { data: allTicketJobs, error: jobsError } = await supabase
        .from('ticket_jobs')
        .select(`
          ticket_id,
          job:jobs (
            id,
            name,
            category,
            bay_requirement
          )
        `)
        .in('ticket_id', ticketIds.length > 0 ? ticketIds : ['00000000-0000-0000-0000-000000000000']);

      if (jobsError) throw jobsError;

      // Build a map of ticket_id -> jobs (in memory, fast)
      const jobsByTicketId = new Map<string, any[]>();
      for (const tj of allTicketJobs || []) {
        if (!tj.job) continue;
        if (!jobsByTicketId.has(tj.ticket_id)) {
          jobsByTicketId.set(tj.ticket_id, []);
        }
        jobsByTicketId.get(tj.ticket_id)!.push(tj.job);
      }

      // Build sections for each bay (all in memory, no more DB calls)
      const sections: SpecialtySection[] = [];

      for (const bay of specialtyBays as SpecialtyBay[]) {
        const bayType = bay.bay_type || bay.bay_category;
        
        // Filter tickets that have matching specialty jobs (in memory)
        const matchingTickets = tickets.filter(ticket => {
          const jobs = jobsByTicketId.get(ticket.id) || [];
          return jobs.some(job => matchesBayType(bayType, job));
        });

        // Transform tickets with jobs and technician info
        const ticketsWithJobs: TicketWithJobs[] = matchingTickets.map(ticket => ({
          ...ticket,
          vehicle: ticket.vehicles,
          jobs: jobsByTicketId.get(ticket.id) || [],
          technician: ticket.assigned_tech_id ? techMap.get(ticket.assigned_tech_id) || null : null
        }));

        // Sort by start time
        ticketsWithJobs.sort((a, b) => {
          const aStart = new Date(a.scheduled_start_time || a.start_time).getTime();
          const bStart = new Date(b.scheduled_start_time || b.start_time).getTime();
          return aStart - bStart;
        });

        sections.push({
          bayCategory: bayType,
          bayName: bay.name,
          tickets: ticketsWithJobs,
        });
      }

      setSpecialtySections(sections);
    } catch (error) {
      console.error('Error fetching specialty data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load specialty bay data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();

    // R36: Realtime subscriptions for tickets + technicians.
    // Server-side filter on shop_id so we only receive this shop's events
    // (was unfiltered before — every shop's events streamed to every
    // client, then a fetchData() refetch hid the noise locally). Channel
    // names also now include shop id to prevent collisions when multiple
    // sessions or shops are active in the same browser.
    //
    // RTC-A5: collapse the two pre-A5 page-level channels
    //   (`specialty-tickets-...`, `specialty-technicians-...`)
    // into ONE consolidated channel with two `.on('postgres_changes', ...)`
    // listener registrations. Pre-A5 each SpecialtyBays mount opened
    // TWO websocket subscriptions for the same shop scope and the same
    // useEffect lifecycle — every event therefore traversed two
    // independent channel objects. Post-A5 the same two listener
    // bodies are registered against ONE channel; behavior is preserved
    // verbatim because Supabase JS dispatches each
    // `.on('postgres_changes', ...)` listener independently regardless
    // of how many channels host it. Listener bodies are byte-identical
    // to the pre-A5 versions; only the channel-creation +
    // `.subscribe(...)` + `removeChannel(...)` boilerplate was
    // consolidated 2→1. The status callback is also consolidated:
    // pre-A5 each of the two `.subscribe(...)` calls had its own
    // `(status, err) => console.error(...)` log line; post-A5 a
    // SINGLE callback covers both listeners. The pre-A5 per-listener
    // log identifiers are preserved in this doctrinal block (R-RT-6
    // ×2) so a future regression that re-introduces per-channel
    // `.subscribe` can re-anchor on the original markers.
    //
    // CRITICAL EVENT-TYPE PRESERVATION: pre-A5 the tickets listener
    // used `event: '*'` (all INSERT/UPDATE/DELETE) but the technicians
    // listener used `event: 'UPDATE'` ONLY (intentionally narrow —
    // technicians changes only trigger a refetch when row contents
    // change, not when techs are added or removed; that flow goes
    // through a separate manage-technicians screen). Post-A5 BOTH
    // event types must be preserved — broadening the technicians
    // listener to `event: '*'` would issue spurious fetchData() calls
    // on technician INSERT/DELETE, which are out of scope for this
    // page's display semantic. The closure test (§B) explicitly
    // anchors `event: 'UPDATE'` for the technicians listener.
    //
    // OUT OF SCOPE — explicitly NOT merged in this round:
    //   - none — SpecialtyBays only owns these two channels.
    //
    // Pre-A5 channel names (preserved here as a doctrinal anchor for
    // the closure test §A — the test scans for them as REMOVED):
    //   `specialty-tickets-${shop_id}`
    //   `specialty-technicians-${shop_id}`
    //
    // Post-A5 channel name:
    //   `specialty-realtime-${shop_id}`
    // SpecialtyBays' useEffect deps include the board day (`selectedDay`), so the
    // merged channel rebuilds on date change exactly as the two
    // pre-A5 channels did.
    const shopId = activeShop?.id;
    if (!shopId) return;

    const specialtyChannel = supabase
      .channel(`specialty-realtime-${shopId}`)
      .on(
        'postgres_changes' as any,
        {
          event: '*',
          schema: 'public',
          table: 'tickets',
          filter: `shop_id=eq.${shopId}`,
        },
        () => {
          fetchData();
        }
      )
      // Subscribe to realtime updates for technicians (for name changes).
      // Event type is intentionally `UPDATE` only — see CRITICAL EVENT-
      // TYPE PRESERVATION block above.
      .on(
        'postgres_changes' as any,
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'technicians',
          filter: `shop_id=eq.${shopId}`,
        },
        () => {
          fetchData();
        }
      )
      .subscribe((status, err) => {
        // RTC-A5: consolidated status callback covers both listeners
        // (tickets / technicians). Pre-A5 each of the two channels
        // had its own `(status, err) => console.error(...)` callback —
        // both anchored on Chronomancer R-RT-6. Post-A5 a single
        // callback surfaces drops for the entire merged channel; the
        // listener-binding error is carried in `err`. Both pre-A5
        // R-RT-6 doctrinal markers are preserved in the doctrinal
        // block above.
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          console.error('[SpecialtyBays] realtime channel status:', status, err);
        }
      });

    return () => {
      supabase.removeChannel(specialtyChannel);
    };
  }, [activeShop?.id, selectedDay]);

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { variant: any; label: string }> = {
      unscheduled: { variant: 'secondary', label: 'Unscheduled' },
      scheduled: { variant: 'default', label: 'Scheduled' },
      in_progress: { variant: 'default', label: 'In Progress' },
      finished: { variant: 'default', label: 'Finished' },
      waiter_released: { variant: 'secondary', label: 'Released' },
    };
    
    const config = statusConfig[status] || { variant: 'secondary', label: status };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <div className={embedded ? "max-w-7xl mx-auto" : "container mx-auto p-6"}>
        <div className="flex items-center justify-center h-96">
          <div className="text-muted-foreground">Loading specialty tasks...</div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("max-w-7xl mx-auto w-full space-y-6", embedded ? "px-6 pt-0" : "container mx-auto px-6")} data-testid="specialty-bays-page">
      <div className="flex items-center justify-between"> 
        <h2 className="text-2xl font-bold mt-0 mb-2">Specialty Bay Manager</h2>
        <Button onClick={fetchData} variant="outline">
          Refresh
        </Button>
      </div>

      {specialtySections.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-muted-foreground">
              <Wrench className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">No specialty bays configured</p>
              <p className="text-sm">Configure specialty bays in the Shop Floor Editor to track specialty tasks</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {specialtySections.map((section) => (
            <Card key={section.bayCategory}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 capitalize">
                  <Wrench className="h-5 w-5" />
                  {section.bayName} - {section.bayCategory}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {section.tickets.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">
                    No tickets with {section.bayCategory} jobs today
                  </p>
                ) : (
                  <div className="space-y-3">
                    {section.tickets.map((ticket) => (
                      <Card key={ticket.id} className="border-l-4 border-l-primary">
                        <CardContent className="pt-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                            <div>
                              <div className="flex items-center gap-2 mb-2">
                                <Car className="h-4 w-4 text-muted-foreground" />
                                <span className="font-medium">Vehicle & Customer</span>
                              </div>
                              <p className="text-sm font-medium">
                                {ticket.vehicle?.year || ''} {ticket.vehicle?.make || ''} {ticket.vehicle?.model || ''}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {ticket.customer_name}
                              </p>
                              {ticket.customer_phone && (
                                <p className="text-xs text-muted-foreground">
                                  {ticket.customer_phone}
                                </p>
                              )}
                            </div>

                            <div>
                              <div className="flex items-center gap-2 mb-2">
                                <Wrench className="h-4 w-4 text-muted-foreground" />
                                <span className="font-medium">Specialty Jobs</span>
                              </div>
                              <div className="space-y-1">
                                {filterJobsForDisplay(ticket.jobs || [], section.bayCategory)
                                  .map((job: any, idx: number) => (
                                    <div key={idx}>
                                      <p className="text-sm font-medium">{job.name}</p>
                                      {job.requires_alignment && (
                                        <Badge variant="secondary" className="text-xs mt-1">
                                          {job.requires_alignment}
                                        </Badge>
                                      )}
                                    </div>
                                  ))}
                              </div>
                            </div>

                            <div>
                              <div className="flex items-center gap-2 mb-2">
                                <Calendar className="h-4 w-4 text-muted-foreground" />
                                <span className="font-medium">Type & Status</span>
                              </div>
                              <Badge variant="outline" className="mb-2">
                                {ticket.type}
                              </Badge>
                              <div>
                                {getStatusBadge(ticket.status)}
                              </div>
                            </div>

                            <div>
                              <div className="flex items-center gap-2 mb-2">
                                <User className="h-4 w-4 text-muted-foreground" />
                                <span className="font-medium">Assigned To</span>
                              </div>
                              <p className="text-sm">
                                {Array.isArray(ticket.technician) ? ticket.technician[0]?.name : ticket.technician?.name || 'Unassigned'}
                              </p>
                            </div>

                            <div>
                              <div className="flex items-center gap-2 mb-2">
                                <Calendar className="h-4 w-4 text-muted-foreground" />
                                <span className="font-medium">Timing</span>
                              </div>
                              {(() => {
                                const startIso = ticket.scheduled_start_time ?? ticket.start_time;
                                if (!startIso) {
                                  return <p className="text-sm text-muted-foreground">Not scheduled</p>;
                                }
                                const startDate = new Date(startIso);
                                const finishDate = ticket.predicted_minutes
                                  ? new Date(startDate.getTime() + (ticket.predicted_minutes || 0) * 60000)
                                  : null;
                                return (
                                  <div className="space-y-1">
                                    <p className="text-sm">
                                      <span className="text-muted-foreground">Start:</span>{' '}
                                      {formatInTimeZone(startDate, shopTimezone, 'h:mm a')}
                                    </p>
                                    {finishDate && (
                                      <p className="text-sm">
                                        <span className="text-muted-foreground">Finish:</span>{' '}
                                        {formatInTimeZone(finishDate, shopTimezone, 'h:mm a')}
                                      </p>
                                    )}
                                  </div>
                                );
                              })()}

                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
